import * as Sentry from '@sentry/browser';
import Backbone from 'backbone';
import _ from 'underscore';

import { KioskHelper } from '@biteinc/business-rules';
import { Log } from '@biteinc/common';
import {
  IntegrationSystem,
  KioskAvailability,
  KioskAvailabilityState,
  KioskBatteryState,
  KioskBluetoothStateHelper,
  KioskDeviceType,
  KioskDeviceTypeHelper,
  KioskExternalTouchDisplayState,
  KioskNetworkInterface,
  KioskNetworkInterfaceHelper,
  KioskNotificationType,
  KioskNotificationTypeHelper,
  KioskOs,
  KioskPaymentTerminalModel,
  KioskPaymentTerminalModelHelper,
  KioskPaymentTerminalState,
  KioskPaymentTerminalStateHelper,
  KioskPeripheralConnectionState,
  KioskPeripheralType,
  KioskPeripheralTypeHelper,
  KioskPusherState,
  KioskPusherStateHelper,
  KioskWifiPowerState,
  KioskWifiPowerStateHelper,
  MdmProvider,
  PrinterCoverState,
  PrinterPaperState,
  PrinterType,
  PrinterTypeHelper,
} from '@biteinc/enums';
import { PaymentHelper, StringHelper, Time } from '@biteinc/helpers';

import { getProcessingNameFromNotificationType } from '../helpers/kiosk_notification_helper';
import { TimeHelper } from '../helpers/time_helper';
import { template } from '../template';

// Chrome Web Store listing for the EloView Link extension, published privately to the
// getbite.com domain. The 32-character id is permanent for the life of the listing, so this
// link stays valid across extension updates.
const ELO_VIEW_LINK_EXTENSION_URL =
  'https://chromewebstore.google.com/detail/penkobfmfbjfcelfpjhgjhlollmmilmm';

// Offered under the "go and find it in EloView yourself" alert. Bite users only: the listing
// is private to the getbite.com domain, so it 404s for the non-Bite org users who also reach
// that alert (any user with ManageKiosks on an EloView kiosk - see mdmIsAccessibleByNonBiteUsers).
// Bootstrap utilities rather than .fineprint: that class is scoped to .kiosk-status in
// kiosk_status.css and has no effect inside #alert-modal.
function eloViewLinkExtensionPromoHtml() {
  return (
    // prettier-ignore
    '<p class="mt-2 text-muted small">' +
      'Install the ' +
      `<a href="${ELO_VIEW_LINK_EXTENSION_URL}" target="_blank" rel="noopener noreferrer">` +
        'Bite EloView Chrome extension' +
      '</a>' +
      ' to open EloView devices straight from Bite Admin.' +
    '</p>'
  );
}

function emojiSpan(emoji, content) {
  return `<span emoji="${emoji}">${content}</span>`;
}

function iconSpan(iconClass, content) {
  return `<span class="icon ${iconClass}">${content}</span>`;
}

function peripheralTemplate(className, name) {
  return (
    // prettier-ignore
    `<div class="section peripheral ${className}">` +
      '<div class="short">' +
        '<div class="header">' +
          '<div class="device-icon"></div>' +
          '<div class="device-identifier">' +
            '<div class="device-name">' +
              `<span class="name-span">${name}</span>` +
            '</div>' +
          '</div>' +
          '<div class="device-light"></div>' +
        '</div>' +
      '</div>' +
      '<div class="long">' +
        '<div class="errors"></div>' +
        '<div class="warnings"></div>' +
        '<div class="details"></div>' +
        '<div class="fineprint"></div>' +
        '<div class="fineprint-extra">' +
          '<div class="toggle"><span class="title"></span></div>' +
          '<div class="details"></div>' +
        '</div>' +
      '</div>' +
    '</div>'
  );
}

const DeviceInfoStruct = function DeviceInfoStruct(name, isInventory) {
  this.name = name;
  this.subtitle = null;
  this.iconUrl = null;
  this.isInventory = !!isInventory;
  this.errors = [];
  this.warnings = [];
  this.menuIsInUse = false;
  this.details = [];
  this.fineprint = [];
  this.extras = [];
  this.shortfineprint = [];
};
DeviceInfoStruct.prototype.addWarning = function addWarning(text, className, silent) {
  this.warnings.push({
    text,
    className,
    silent,
  });
};

app.KioskStatusView = Backbone.View.extend({
  _expanded: false,
  template: template(
    // prettier-ignore
    `<div class="kiosk-status">` +
        `<div class="status-row">` +
          `<div class="section ipad">` +
            `<div class="short">` +
              `<div class="header">` +
                `<div class="device-icon"></div>` +
                `<div class="device-identifier">` +
                  `<div class="device-name">` +
                    `<span class="name-span"></span>` +
                    `<span class="in-use-badge"></span>` +
                  `</div>` +
                `</div>` +
                `<div class="device-light"></div>` +
                `<div class="btn-group on-off"></div>` +
                `<div class="timestamp"></div>` +
                `<div class="dropdown actions">` +
                  `<button type="button" class="btn settings" data-bs-toggle="dropdown" aria-label="Settings">•••</button>` +
                  `<ul class="dropdown-menu collapsed dropdown-menu-right kiosk-actions">` +
                    `<li class="item edit-kiosk"><a>Edit Kiosk ${app.HtmlHelper.resellerRightIcon}</a></li>` +
                  `</ul>` +
                `</div>` +
              `</div>` +
              `<div class="fineprint"></div>` +
            `</div>` +
            `<div class="long">` +
              `<div class="errors"></div>` +
              `<div class="warnings"></div>` +
              `<div class="details"></div>` +
              `<div class="fineprint"></div>` +
              `<div class="fineprint-extra">` +
                `<div class="toggle"><span class="title"></span></div>` +
                `<div class="details"></div>` +
              `</div>` +
            `</div>` +
          `</div>` +
        `</div>` +
        `<div class="status-row">${
          peripheralTemplate('printer', 'Printer')
          }${peripheralTemplate('payment', 'Payment')
        }</div>` +
      `</div>`,
  ),
  turnOffDropdownTemplate: template($('#turnoff-dropdown-template').html()),

  initialize(options) {
    this._kiosk = options.kiosk;
    this._status = options.status;
    this._editKioskCallback = options.editKioskCallback;
  },

  setExpanded(expanded) {
    if (this._expanded === expanded) {
      return;
    }

    this._expanded = expanded;
    if (this._expanded) {
      // Fetch MDM status lazily only once the kiosk cell view gets expanded
      this._kiosk?.fetchMdmStatus();

      this.$('.short .fineprint').slideUp(400, 'easeInOutCubic');
      this.$('.long').slideDown(400, 'easeInOutCubic');
    } else {
      this.$('.short .fineprint').slideDown(400, 'easeInOutCubic');
      this.$('.long').slideUp(400, 'easeInOutCubic');
    }
  },

  _getLocation() {
    if (app.location) {
      return app.location;
    }
    const model = this._kiosk || this._status;
    if (!app.orgList) {
      return app.locationList.get(model.get('locationId'));
    }
    return app.orgList.getLocationById(model.get('locationId'));
  },

  _peerId() {
    if (this._kiosk) {
      return this._kiosk.displayName();
    }
    if (this._status.get('kioskId')) {
      const kiosk = app.kioskList.get(this._status.get('kioskId'));
      if (kiosk) {
        return kiosk.displayName();
      }
    }
    if (this._status.get('prefix')) {
      return `Kiosk ${this._status.get('prefix')}`;
    }
    return '';
  },

  _bodyWasClicked() {
    this.setExpanded(!this._expanded);
    if (this._expanded) {
      this.trigger(app.KioskStatusView.Events.ViewWillExpand);
    } else {
      this.trigger(app.KioskStatusView.Events.ViewWillCollapse);
    }
    return false;
  },

  _isVeryOldStatusAge(ageDuration) {
    return ageDuration > 30 * Time.MINUTE;
  },

  _isOldStatusAge(ageDuration) {
    return ageDuration > 5 * Time.MINUTE;
  },

  _classForStatusAge(startTimestamp) {
    const diff = Date.now() - startTimestamp;
    if (this._isVeryOldStatusAge(diff)) {
      return 'very-old';
    }
    if (this._isOldStatusAge(diff)) {
      return 'old';
    }
    // Anything less than 5 minutes.
    return '';
  },

  _updateTimestamp($timestamp) {
    // Bail if the model has been updated and this is a dead view
    if (this._kiosk) {
      if (!this._kiosk.collection) {
        clearTimeout(this._timestampTimer);
        return;
      }
    } else if (!this._status.collection) {
      clearTimeout(this._timestampTimer);
      return;
    }

    const updatedAt = this._status.get('lastStatusReceivedAt') || this._status.get('updatedAt');
    if (this._kiosk) {
      $timestamp.text(`${TimeHelper.timeAgoStrFromNow(updatedAt)} ago`);
      $timestamp.toggleClass(this._classForStatusAge(updatedAt), true);
      $timestamp.attr('title', TimeHelper.format(updatedAt, TimeHelper.FullDateFormat));
      this._timestampTimer = setTimeout(() => {
        this._updateTimestamp($timestamp);
      }, Time.SECOND);
    } else {
      // if we are showing historical status then we just want to show the time without any warnings
      $timestamp.text(
        `${Time.moment(updatedAt, app.location.get('timezone')).format('HH:mm:ss z')}`,
      );
    }
  },

  _generateDeviceStatsString(deviceHasBattery, deviceStatus) {
    const parts = [];

    if (deviceHasBattery) {
      if (deviceStatus.batteryLevel === -1) {
        parts.push(emojiSpan('🔋', 'unknown'));
      } else {
        let levelStr = (deviceStatus.batteryLevel * 100).toFixed(2);
        if (parseInt(levelStr, 10) === parseFloat(levelStr, 10)) {
          levelStr = `${parseInt(levelStr, 10)}`;
        }
        parts.push(emojiSpan('🔋', `${levelStr}%`));
      }
    }

    if (_.has(deviceStatus, 'brightnessLevel')) {
      parts.push(emojiSpan('🔆', `${Math.floor(deviceStatus.brightnessLevel * 100)}%`));
    }

    return parts.join(' | ');
  },

  _generateMenuVersionString(deviceStatus) {
    const parts = [];
    if (deviceStatus.menuTimestamp > 0) {
      parts.push(`Menu Data: ${TimeHelper.format(deviceStatus.menuTimestamp)}`);
    }
    // Don't show gcn/vitrine build versions to customers. We want to release these apps as often
    // as possible without having to explain minor changes to everyone.
    if (app.sessionUser.isBite()) {
      if (deviceStatus.gcnVersionName) {
        const prefix = 'biteinc-gcn.';
        const gcnVersion = deviceStatus.gcnVersionName.startsWith(prefix)
          ? deviceStatus.gcnVersionName.substring(prefix.length)
          : deviceStatus.gcnVersionName;
        parts.push(`gcn v${gcnVersion}`);
      } else if (deviceStatus.vitrineVersionName) {
        const prefix = 'biteinc-vitrine.';
        const gcnVersion = deviceStatus.vitrineVersionName.startsWith(prefix)
          ? deviceStatus.vitrineVersionName.substring(prefix.length)
          : deviceStatus.vitrineVersionName;
        parts.push(`vitrine v${gcnVersion}`);
      } else if (deviceStatus.menuJSVersion > 0) {
        parts.push(`Menu JS: ${TimeHelper.format(deviceStatus.menuJSVersion)}`);
      }
    }
    return parts.join(', ');
  },

  _addDropdownAction($dropdown, title, options) {
    let fullTitle = title;
    if (options.rightIcon) {
      fullTitle += ` ${options.rightIcon}`;
    }

    const $action = $(`<li class="item"><a>${fullTitle}</a></li>`);
    if (options.isCritical) {
      $action.addClass('critical');
    }

    if (options.clickHandler) {
      $action.click(() => {
        if (options.confirmation) {
          options.confirmation(options.clickHandler);
        } else {
          options.clickHandler();
        }
      });
    } else if (options.url) {
      $action.find('a').attr('href', options.url);
      $action.find('a').attr('target', '_blank');
    } else {
      Log.error('bad dropdown options', title);
    }

    $dropdown.append($action);
  },

  _showConfigUpdateModal() {
    const detailsView = new app.BaseDetailsView({
      title: `Send Kiosk Config to ${this._peerId()}`,
      saveButtonTitles: ['Send Config', 'Sending', 'Sent!'],
      model: new app.KioskConfig({}, { kiosk: this._kiosk }),
      schema: app.KioskConfig.Schema(),
      callback(model) {
        Log.debug('got model', model);
        app.showSavedToast('Success!');
      },
    });
    app.modalManager.showModalWithView(detailsView);
  },

  _addDropdownActionForNotification($dropdown, notificationType, options) {
    const self = this;
    let onClickHandler = function onClickHandler() {
      if (
        !self._status.has('pusherState') ||
        self._status.get('pusherState') !== KioskPusherState.Subscribed
      ) {
        const shouldProceed = confirm(
          `This kiosk isn't connected to Pusher so it won't receive this notification. Do you want to proceed anyway?`,
        );
        if (!shouldProceed) {
          return;
        }
      }

      let data = null;
      if (KioskNotificationType.TestPrint === notificationType) {
        const message = prompt('Please enter a test message.', 'Test Message');
        if (!(message || '').length) {
          return;
        }
        data = {
          message,
          destination: 0,
        };
      }

      if (KioskNotificationType.UpdatePaymentTerminal === notificationType) {
        if (app.location.hasIntegrationWithSystem(IntegrationSystem.Heartland)) {
          const terminalId = prompt(
            "Please enter the kiosk's terminalId. Optional. BE VERY CAREFUL.",
            '',
          );
          if (!_.isString(terminalId)) {
            return;
          }
          const updateParams = {};
          if (terminalId.trim().length) {
            updateParams.terminalId = terminalId.trim();
          }
          data = {
            updateParams: {
              terminalId: terminalId.trim(),
            },
          };
        } else if (app.location.hasIntegrationWithSystem(IntegrationSystem.FreedomPayKiosk)) {
          const palFileUrl = prompt('Please enter the PAL package file URL', '');
          if (!palFileUrl) {
            return;
          }
          if (!StringHelper.isUrl(palFileUrl)) {
            alert('Invalid URL');
            onClickHandler();
            return;
          }
          data = {
            updateParams: {
              palFileUrl,
            },
          };
        }
      }

      app.showSavedToast(getProcessingNameFromNotificationType(notificationType));

      self._kiosk.notifyDevice(notificationType, data, {
        error() {
          app.showSavedToast('Failed. :(');
        },
        success() {
          switch (notificationType) {
            case KioskNotificationType.UpdatePaymentTerminal:
            case KioskNotificationType.EnableAsam:
            case KioskNotificationType.DisableAsam:
            case KioskNotificationType.RequestStatus:
              app.showSavedToastAndReload('Success! Reloading in 10sec.', true, 10000);
              break;
            default:
              app.showSavedToast('Success!');
          }
        },
      });
    };

    switch (notificationType) {
      case KioskNotificationType.ConfigUpdate:
        onClickHandler = self._showConfigUpdateModal.bind(this);
        break;
    }

    return this._addDropdownAction($dropdown, KioskNotificationTypeHelper.name(notificationType), {
      isCritical:
        notificationType === KioskNotificationType.RestartApp ||
        notificationType === KioskNotificationType.AppLogout ||
        notificationType === KioskNotificationType.AppLogin ||
        notificationType === KioskNotificationType.ReturnToConfig ||
        notificationType === KioskNotificationType.RebootPaymentTerminal,
      clickHandler: onClickHandler,
      ...(options || {}),
    });
  },

  _updateAvailabilityState(state, message) {
    app.showSavedToast(message);
    this._kiosk.save(
      {
        availability: state,
      },
      {
        patch: true,
        error() {
          app.showSavedToast('Failed. :(');
        },
        success() {
          app.showSavedToastAndReload('Success! Reloading in 10sec.', true, 10000);
        },
      },
    );
  },

  _removeDropdownActionsEditKiosk() {
    const $ipad = this.$('.ipad');
    $ipad.find('.dropdown.actions .dropdown-menu .edit-kiosk').remove();
  },

  _cleanupDropdownActions() {
    const $ipad = this.$('.ipad');
    const $actionsDropdown = $ipad.find('.dropdown.actions');
    if (!$actionsDropdown.find('.dropdown-menu').children().length) {
      $actionsDropdown.remove();
    }
  },

  _renderOnOffButton() {
    const self = this;

    if (this._kiosk.get('availability') === KioskAvailability.ON) {
      const $turnOffDropdown = $(self.turnOffDropdownTemplate());
      $turnOffDropdown.find('.until-next a').click(() => {
        self._updateAvailabilityState(
          KioskAvailability.OFF_UNTIL_NEXT,
          'Turning off until tomorrow...',
        );
        $turnOffDropdown.toggleClass('open');
        return false;
      });
      $turnOffDropdown.find('.indefinitely a').click(() => {
        self._updateAvailabilityState(
          KioskAvailability.OFF_INDEFINITELY,
          'Turning off indefinitely...',
        );
        $turnOffDropdown.toggleClass('open');
        return false;
      });

      self.$('.btn-group.on-off').append($turnOffDropdown);

      if (app.sessionUser.canControlKiosks()) {
        $turnOffDropdown.find('button').click((event) => {
          $turnOffDropdown.toggleClass('open');
          event.stopPropagation();
        });
      } else {
        $turnOffDropdown.find('button').prop('disabled', true);
      }
    } else {
      const $button = $(
        '<button type="button" class="btn btn-secondary btn-sm turn-on">Turn On</button>',
      );

      if (app.sessionUser.canControlKiosks()) {
        $button.click(() => {
          const reqId = $button.initLoadingButton('Turn On', 'Turning On', 'Turned On');
          self._kiosk.save(
            {
              availability: KioskAvailability.ON,
            },
            {
              patch: true,
              error() {
                $button.loadingDidFinishWithError(reqId);
              },
              success() {
                $button.loadingDidFinishSuccessfully(reqId);
              },
            },
          );
          return false;
        });
      } else {
        $button.prop('disabled', true);
      }

      self.$('.btn-group.on-off').append($button);
    }
  },

  _renderCopySerialButton() {
    const self = this;
    const serialNumber = self._kiosk.get('serialNumber');

    if (!serialNumber || !navigator.clipboard || !navigator.clipboard.writeText) {
      return;
    }

    if (!app.sessionUser.canControlKiosks()) {
      return;
    }

    const $button = $(
      '<button type="button" class="btn btn-secondary btn-sm copy-serial" title="Copy Serial Number">Copy Serial</button>',
    );

    $button.click(() => {
      navigator.clipboard
        .writeText(serialNumber)
        .then(() => {
          app.showSavedToast('Serial number copied to clipboard!');
        })
        .catch(() => {
          app.showSavedToast('Failed to copy serial number');
        });
      return false;
    });

    self.$('.btn-group.on-off').append($button);
  },

  _renderDevice($el, info) {
    const nonSilentLineCountByKey = {};
    let hasTooltips = false;

    // Errors, warnings, details, fine print
    if (info.iconUrl) {
      $el.find('.device-icon').css('background-image', `url(${info.iconUrl})`);
    }
    $el.find('.device-name span.name-span').text(info.name);
    if (info.menuIsInUse) {
      $el.find('.device-name span.in-use-badge').text('in use');
    } else {
      $el.find('.device-name span.in-use-badge').remove();
    }
    if (info.subtitle) {
      $el.find('.device-name').append(`<span class="subtitle-span">${info.subtitle}</span>`);
    }
    const keysByLocation = {
      short: ['fineprint'],
      long: ['errors', 'warnings', 'details', 'fineprint'],
    };
    _.each(keysByLocation, (keys, location) => {
      _.each(keys, (key) => {
        nonSilentLineCountByKey[key] = 0;
        const lines = info[('short' === location ? 'short' : '') + key];
        _.each(lines, (line) => {
          const $span = $('<span></span>');
          if (_.isObject(line)) {
            if (line.className) {
              $span.addClass(line.className);
            }
            $span.html(line.text.split('\n').join('<br />'));
            if (line.tooltip) {
              hasTooltips = true;
              $span.append(app.renderTooltip(line.tooltip));
            }
            if (!line.silent) {
              nonSilentLineCountByKey[key]++;
            }
          } else {
            nonSilentLineCountByKey[key]++;
            $span.html(line.split('\n').join('<br />'));
          }
          $el.find(`.${location}>.${key}`).append($span);
        });
      });
    });

    // Overall status dot.
    if (nonSilentLineCountByKey.errors > 0) {
      $el.find('.device-name span.name-span').toggleClass('errors', true);
    } else if (nonSilentLineCountByKey.warnings > 0) {
      $el.find('.device-name span.name-span').toggleClass('warnings', true);
    } else if (!info.isInventory) {
      $el.find('.device-name span.name-span').toggleClass('good', true);
    }

    // Extras
    $el.find('.fineprint-extra .toggle .title').text(info.extraTitle);
    if (info.extras.length) {
      const $extraList = $el.find('.fineprint-extra .details');
      _.each(info.extras, (extraLine) => {
        $extraList.append(`<span>${extraLine}</span>`);
      });
      $extraList.hide();

      const $arrow = $('<span class="arrow">▼</span>');
      const $extraToggle = $el.find('.fineprint-extra .toggle');
      $extraToggle.append($arrow);
      $extraToggle.click(() => {
        if ($extraList.is(':visible')) {
          $extraList.slideUp();
          $arrow.css('transform', 'rotate(0deg)');
        } else {
          $extraList.slideDown();
          $arrow.css('transform', 'rotate(180deg)');
        }
        return false;
      });
      $extraToggle.find('.title').toggleClass('has-extras', true);
    }

    if (hasTooltips) {
      app.activateTooltips($el);
    }
  },

  _renderPrinterStatus($el, printerStatus, expectedMacAddress) {
    const info = new DeviceInfoStruct(`Printer`);
    if (this._kiosk) {
      const printerType = this._kiosk.get('printerType');
      info.iconUrl = PrinterTypeHelper.printerIconUrlFromType(printerType);
    }

    if ((expectedMacAddress || '').length) {
      if (!printerStatus || printerStatus.macAddress !== expectedMacAddress) {
        info.warnings.push(`Expected: ${expectedMacAddress}`);
      }
    }

    if (printerStatus) {
      // Connectivity
      const isConnected =
        KioskPeripheralConnectionState.CONNECTED === printerStatus.connectionState;
      const connectivity = {
        className: 'connected-dot',
        text: `${isConnected ? '' : 'dis'}connected`,
      };
      if (isConnected) {
        connectivity.className += ' online';
        info.details.push(connectivity);
      } else {
        connectivity.className += ' offline';
        info.errors.push(connectivity);
      }
      if ((printerStatus.disconnectedSince || 0) > 0) {
        info.errors.push(
          `No stable connection since ${TimeHelper.format(printerStatus.disconnectedSince)}`,
        );
      }

      // Stability
      if ((printerStatus.consecutiveFailedJobCount || 0) > 0) {
        const failCount = printerStatus.consecutiveFailedJobCount;
        info.errors.push(`${failCount} consecutive fail${failCount === 1 ? '' : 's'}`);
      }

      // Paper
      if (PrinterPaperState.Low === printerStatus.paperState) {
        info.warnings.push('Paper Low');
      } else if (PrinterPaperState.Empty === printerStatus.paperState) {
        info.errors.push('Paper Empty');
      }

      // Cover
      if (PrinterCoverState.Open === printerStatus.coverState) {
        info.errors.push('Cover Open');
      }

      info.details.push(printerStatus.macAddress);

      // If we are getting printer status when we are not supposed to be
      if (this._kiosk && !this._kiosk.get('printerType')) {
        info.errors.push('Printer is configured on the kiosk but not in Bite Admin');
        info.name = `Unexpected ${info.name}`;
        $el.toggleClass('no-status', true);
      }
    } else if (this._kiosk?.get('printerType')) {
      info.errors.push('Printer is misconfigured on the kiosk');
      info.name = `Invalid ${info.name}`;
      $el.toggleClass('no-status', true);
    } else {
      info.name = `No ${info.name}`;
      $el.toggleClass('no-status', true);
    }

    this._renderDevice($el, info);
  },

  _renderPrinterInventory($el, model) {
    const info = new DeviceInfoStruct('Printer', true);

    if (model.get('printerType') === PrinterType.NoPrinter || !model.get('printerType')) {
      info.name = 'No Printer';
    } else {
      info.iconUrl = PrinterTypeHelper.printerIconUrlFromType(model.get('printerType'));
      info.fineprint.push(`Type: ${PrinterTypeHelper.name(model.get('printerType'))}`);
      info.fineprint.push(`MAC Address: ${model.get('printerMacAddress')}`);
    }

    this._renderDevice($el, info);
  },

  _renderPaymentStatus($el, paymentStatus) {
    const info = new DeviceInfoStruct('Payment');
    if (paymentStatus || this._kiosk) {
      const currentModel = (paymentStatus || {}).type || KioskPaymentTerminalModel.None;
      if (currentModel !== KioskPaymentTerminalModel.None) {
        info.subtitle = `${KioskPaymentTerminalModelHelper.name(currentModel)}`;
      }
      info.iconUrl = KioskPaymentTerminalModelHelper.iconUrl(
        paymentStatus ? paymentStatus.type : this._kiosk.get('paymentTerminalModel'),
      );
    }

    if (this._kiosk) {
      const expectedModel = this._kiosk.get('paymentTerminalModel');
      const currentModel = (paymentStatus || {}).type || KioskPaymentTerminalModel.None;
      if (expectedModel !== currentModel) {
        info.warnings.push(`Expected: ${KioskPaymentTerminalModelHelper.name(expectedModel)}`);
      }
    }

    if (paymentStatus) {
      const stateLine = {
        className: 'connected-dot',
        text: KioskPaymentTerminalStateHelper.name(paymentStatus.state),
      };
      switch (paymentStatus.state) {
        case KioskPaymentTerminalState.READY:
        case KioskPaymentTerminalState.TRANSACTION:
          stateLine.className += ' online';
          info.details.push(stateLine);
          break;
        case KioskPaymentTerminalState.UPDATING:
          stateLine.className += ' warning';
          info.warnings.push(stateLine);
          break;
        default:
          stateLine.className += ' offline';
          info.errors.push(stateLine);
          break;
      }

      if ((paymentStatus.updatedAt || 0) > 0) {
        info.details.push(`Updated on: ${TimeHelper.format(paymentStatus.updatedAt)}`);
      }
      if ((paymentStatus.disconnectedSince || 0) > 0) {
        info.errors.push(
          `No stable connection since ${TimeHelper.format(paymentStatus.disconnectedSince)}`,
        );
      }
    } else {
      info.name = `No ${info.name}`;
      $el.toggleClass('no-status', true);
    }

    this._renderDevice($el, info);
  },

  _renderTouchDisplayStatus($el, displayStatus) {
    const info = new DeviceInfoStruct('Touch Display');

    if (displayStatus?.externalTouchDisplayState) {
      const state = displayStatus.externalTouchDisplayState;
      const stateLine = {
        className: 'connected-dot',
        text:
          state === KioskExternalTouchDisplayState.Connected
            ? 'connected'
            : state === KioskExternalTouchDisplayState.Disconnected
              ? 'disconnected'
              : state === KioskExternalTouchDisplayState.Connecting
                ? 'connecting'
                : 'not required',
      };

      switch (state) {
        case KioskExternalTouchDisplayState.Connected:
          stateLine.className += ' online';
          info.details.push(stateLine);
          break;
        case KioskExternalTouchDisplayState.Connecting:
          stateLine.className += ' warning';
          info.warnings.push(stateLine);
          break;
        case KioskExternalTouchDisplayState.Disconnected:
          stateLine.className += ' offline';
          info.errors.push(stateLine);
          break;
        case KioskExternalTouchDisplayState.NotRequired:
          info.name = `No ${info.name}`;
          $el.toggleClass('no-status', true);
          this._renderDevice($el, info);
          return;
        default:
          stateLine.className += ' offline';
          info.errors.push(stateLine);
          break;
      }

      if (displayStatus.isTouchDisplayFaulty) {
        info.errors.push(
          'Touch Display Faulty. Make sure the screen is connected to the kiosk and is turned on',
        );
      }
    }
    this._renderDevice($el, info);
  },

  _renderPaymentInventory($el, model) {
    const info = new DeviceInfoStruct('Payment', true);

    if (model.get('paymentTerminalModel')) {
      info.iconUrl = KioskPaymentTerminalModelHelper.iconUrl(model.get('paymentTerminalModel'));
      info.subtitle = KioskPaymentTerminalModelHelper.name(model.get('paymentTerminalModel'));
      if (model.hasStr('paymentTerminalSerialNumber')) {
        info.fineprint.push(`Serial: ${model.get('paymentTerminalSerialNumber')}`);
      }
    } else {
      info.name = 'No Payment';
    }

    this._renderDevice($el, info);
  },

  _renderAdditionalPeripheral(className, name) {
    let $row = this.$('.status-row.appendix').last();
    if (!$row.length || $row.children().length === 2) {
      $row = $('<div class="status-row appendix"></div>');
      this.$el.find('.kiosk-status').append($row);
    }

    const $cell = $(peripheralTemplate(className, name));
    $row.append($cell);
    if (!this._expanded) {
      $cell.find('.long').css('display', 'none');
    }
    return $cell;
  },

  _peripheralParamsFromType(peripheralType) {
    const name = KioskPeripheralTypeHelper.name(peripheralType);
    switch (peripheralType) {
      case KioskPeripheralType.Scanner:
        return { className: 'scanner', deviceName: name };
      case KioskPeripheralType.Scale:
        return { className: 'scale', deviceName: name };
      case KioskPeripheralType.MSR:
        return { className: 'msr', deviceName: name };
      case KioskPeripheralType.HID:
        return { className: 'hid', deviceName: name };
      case KioskPeripheralType.StormAudioNav:
        return { className: 'storm', deviceName: name };
      case KioskPeripheralType.Display:
        return { className: 'touch-display', deviceName: name };
      default:
        Log.error('unsupported peripheral', peripheralType);
        return null;
    }
  },

  _renderPrinterPeripheralStatus(peripheralStatus) {
    const peripheralParams = this._peripheralParamsFromType(peripheralStatus.deviceType);

    const info = new DeviceInfoStruct(peripheralParams.deviceName);
    const isConnected =
      KioskPeripheralConnectionState.CONNECTED === peripheralStatus.connectionState;
    const connectivity = {
      className: 'connected-dot',
      text: `${isConnected ? '' : 'dis'}connected`,
    };
    if (isConnected) {
      connectivity.className += ' online';
      info.details.push(connectivity);
    } else {
      connectivity.className += ' offline';
      info.errors.push(connectivity);
    }
    if ((peripheralStatus.disconnectedSince || 0) > 0) {
      info.errors.push(
        `No stable connection since ${TimeHelper.format(peripheralStatus.disconnectedSince)}`,
      );
    }

    let target = peripheralStatus.target;
    if (target.indexOf('TCPS:') === 0) {
      target = target.substr(5);
    }
    info.details.push(target);

    const $peripheral = this._renderAdditionalPeripheral(
      peripheralParams.className,
      peripheralParams.deviceName,
    );
    this._renderDevice($peripheral, info);
  },

  _renderUsbPeripheralStatus(peripheralStatus, peripheralType) {
    const peripheralParams = this._peripheralParamsFromType(peripheralType);

    const info = new DeviceInfoStruct(peripheralParams.deviceName);
    const isConnected = !!peripheralStatus?.connected;
    const connectivity = {
      className: 'connected-dot',
      text: `${isConnected ? '' : 'dis'}connected`,
    };
    if (isConnected) {
      connectivity.className += ' online';
      info.details.push(connectivity);
    } else {
      connectivity.className += ' offline';
      info.errors.push(connectivity);
    }
    if ((peripheralStatus?.disconnectedSince || 0) > 0) {
      info.errors.push(
        `No stable connection since ${TimeHelper.format(peripheralStatus?.disconnectedSince)}`,
      );
    }
    if (peripheralType === KioskPeripheralType.Display && peripheralStatus?.isTouchDisplayFaulty) {
      info.errors.push(
        'Touch Display Faulty. Make sure the screen is connected to the kiosk and is turned on',
      );
    }
    const $peripheral = this._renderAdditionalPeripheral(
      peripheralParams.className,
      peripheralParams.deviceName,
    );
    this._renderDevice($peripheral, info);
  },

  _renderPosStatus(posStatus) {
    const info = new DeviceInfoStruct('POS');
    const status = posStatus.status;
    const connectivity = {
      className: 'connected-dot',
      text: status,
    };

    switch (status) {
      case 'available':
      case 'connected':
        connectivity.className += ' online';
        info.details.push(connectivity);
        break;
      case 'initializing':
        connectivity.className += ' warning';
        info.warnings.push(connectivity);
        break;
      case 'disconnected':
      case 'stopped':
      case 'error':
      default:
        connectivity.className += ' offline';
        info.errors.push(connectivity);
        break;
    }

    const $peripheral = this._renderAdditionalPeripheral('pos', 'POS');
    this._renderDevice($peripheral, info);
  },

  render() {
    this.$el.html(this.template());

    this.$el.off('click');
    this.$el.click(this._bodyWasClicked.bind(this));

    // Used later to toggle nested cells.
    this.$el.attr('id', this._kiosk?.id || this._status.get('kioskId'));
    this.$el.addClass('kiosk-card');

    const $ipad = this.$('.ipad');
    const $printer = this.$('.printer');
    const $payment = this.$('.payment');
    const isBite = app.sessionUser.isBite();
    const isBiteSupport = app.sessionUser.isBiteSupport();
    const isBiteEng = app.sessionUser.isBiteEng();
    const isBiteOps = app.sessionUser.isBiteOps();
    const location = this._getLocation();
    const timezone = location?.get('timezone') || Time.BITE_TZ;

    const $actionsDropdown = $ipad.find('.dropdown.actions');
    $actionsDropdown.click((event) => {
      $ipad.find('.dropdown.actions').toggleClass('open');
      event.stopPropagation();
    });
    if (this._editKioskCallback && app.location) {
      $actionsDropdown.find('.edit-kiosk').click(() => {
        this._editKioskCallback();
      });
    } else {
      $actionsDropdown.find('.edit-kiosk').remove();
    }

    if (this._kiosk && app.location) {
      this._renderOnOffButton();
      this._renderCopySerialButton();
    }
    if (!this._expanded) {
      this.$('.long').css('display', 'none');
    }

    let deviceIdentifierHtml = '';

    if (!app.sessionUser.canManageKiosks() || (!this._kiosk && !isBite)) {
      this._removeDropdownActionsEditKiosk();
    }

    if (this._status) {
      this.$el.addClass(`status-${this._status.id}`);
      const deviceStatus = this._status.get('deviceStatus');

      if (this._timestampTimer) {
        clearTimeout(this._timestampTimer);
      }

      const info = new DeviceInfoStruct(this._peerId());

      let kioskDeviceType = this._kiosk?.get('deviceType');
      if (!kioskDeviceType) {
        // kioskDeviceType will be missing if this is an untracked device
        // i.e. someone logged into the native app with their bite admin credentials
        // We only allow this on Elo androids and iOS device
        if (deviceStatus.os === KioskOs.Android) {
          kioskDeviceType = KioskDeviceType.AndroidElo22;
        }
        kioskDeviceType = KioskDeviceType.iPadPro;
      }

      const { displayModelName, statusSummary, warnings } = KioskHelper.parseDeviceStatus(
        deviceStatus,
        kioskDeviceType,
      );
      info.iconUrl = KioskDeviceTypeHelper.kioskIconUrlFromType(
        KioskHelper.deviceTypeFromModelName(displayModelName, kioskDeviceType),
      );

      this._updateTimestamp($ipad.find('.timestamp'));
      if (this._kiosk) {
        // if there's no kiosk, then we are just showing historical status so this warning could be
        // misleading
        const diff =
          Date.now() - (this._status.get('lastStatusReceivedAt') || this._status.get('updatedAt'));
        if (this._isVeryOldStatusAge(diff)) {
          info.errors.push('Kiosk has not reported status in a long time.');
        } else if (this._isOldStatusAge(diff)) {
          info.warnings.push('Kiosk has not reported status recently.');
        }

        // Clock drift — device wall clock vs the server's status-receive time (possible NTP
        // failure). 2-minute threshold kept in sync with v2 parseDeviceWarnings.
        const lastStatusReceivedAt =
          this._status.get('lastStatusReceivedAt') || this._status.get('updatedAt');
        if (deviceStatus.deviceTime && lastStatusReceivedAt) {
          const driftMs = Math.abs(lastStatusReceivedAt - deviceStatus.deviceTime);
          if (driftMs > 2 * 60 * 1000) {
            info.warnings.push(`Clock Drift (off by ~${Math.round(driftMs / 60000)}m)`);
          }
        }

        // Wrong timezone — device IANA zone differs from the location's expected zone. Use the raw
        // location timezone (not the BITE_TZ fallback) to avoid false positives when unset.
        const expectedTimezone = location?.get('timezone');
        if (
          deviceStatus.timezone &&
          expectedTimezone &&
          deviceStatus.timezone !== expectedTimezone
        ) {
          info.warnings.push(
            `Wrong Timezone (expected ${expectedTimezone}, got ${deviceStatus.timezone})`,
          );
        }
      }

      if (deviceStatus.availability === KioskAvailabilityState.Loading) {
        info.errors.push('Starting Up');
      } else if (deviceStatus.availability === KioskAvailabilityState.MaintenanceMode) {
        let ms = 'In Maintenance Mode';
        if (deviceStatus.downSince) {
          ms += ' Since ';
          const downSince = Time.moment(deviceStatus.downSince, timezone);
          if (downSince.isBefore(TimeHelper.localMoment().startOf('day'))) {
            ms += `on ${downSince.format('MM/DD/YY')} at `;
          }
          ms += downSince.format('HH:mm');
        }
        info.errors.push(ms);
      } else if (deviceStatus.availability === KioskAvailabilityState.TurnedOff) {
        let turnedOffAtStr = 'Turned Off';
        if (this._kiosk && this._kiosk.get('availability')) {
          if (this._kiosk.get('availability') === KioskAvailability.OFF_UNTIL_NEXT) {
            turnedOffAtStr += ' (Until Next Service)';
          } else {
            turnedOffAtStr += ' (Indefinitely)';
          }
        }
        if (deviceStatus.turnedOffAt) {
          const turnedOffAt = Time.moment(deviceStatus.turnedOffAt, timezone);
          if (turnedOffAt.isBefore(TimeHelper.localMoment().startOf('day'))) {
            turnedOffAtStr += ` on ${turnedOffAt.format('MM/DD/YY')}`;
          }
          turnedOffAtStr += ` at ${turnedOffAt.format('HH:mm')}`;
        }
        info.warnings.push(turnedOffAtStr);
      }

      if (this._status.get('isUsingMenu')) {
        info.menuIsInUse = true;
      } else {
        const paymentTerminalStatus = this._status.get('paymentTerminalStatus');
        if (KioskPaymentTerminalState.TRANSACTION === paymentTerminalStatus?.state) {
          info.errors.push('Stuck in Transaction State!');
        }
      }

      if (isBite) {
        if (this._kiosk && this._kiosk.hasStr('notes')) {
          const notes = `${app.HtmlHelper.biteRightIcon} Notes: ${this._kiosk.get('notes')}`;
          info.shortfineprint.push(notes);
          info.fineprint.push(notes);
        }
      }
      if (this._kiosk) {
        info.fineprint.push(`S/N: ${this._kiosk.get('serialNumber')}`);
      }
      const { externalIpAddress } = this._status.get('deviceStatus');
      if (app.location) {
        if (this._kiosk?.get('internalName')) {
          info.fineprint.push(this._kiosk?.get('internalName'));
        }
        info.fineprint.push(statusSummary);
        if (externalIpAddress) {
          info.fineprint.push(`External IP Address: ${externalIpAddress}`);
        }
      } else {
        if (this._kiosk?.get('internalName')) {
          deviceIdentifierHtml += `<span class="subtitle">${this._kiosk?.get('internalName')}</span><br />`;
        }
        deviceIdentifierHtml += `<span class="subtitle">${statusSummary}</span>`;
        if (externalIpAddress && isBite) {
          deviceIdentifierHtml += `<br /><span class="subtitle">External IP Address: ${externalIpAddress}</span>`;
        }
      }
      if (warnings.length && isBite) {
        info.warnings.push(
          ...warnings.map((warning) => {
            return warning.message;
          }),
        );
      }
      const menuVersionString = this._generateMenuVersionString(deviceStatus);
      if (menuVersionString.length) {
        info.fineprint.push(menuVersionString);
      }

      if (isBite && this._kiosk) {
        const mdmStatus = this._kiosk.getMdmStatusSummary();
        if (mdmStatus.length) {
          info.fineprint.push(`${app.HtmlHelper.biteRightIcon} ${mdmStatus}`);
        }
      }

      info.details.push(`Uptime: ${TimeHelper.roughDurationFromTimestamp(deviceStatus.uptime)}`);
      info.details.push(this._generateDeviceStatsString(this._status.isIOS(), deviceStatus));

      // Communications stats.
      const communicationsParts = [];

      // Reachability
      const networkInterface = KioskNetworkInterfaceHelper.stringFromEnum(
        deviceStatus.currentNetworkInterface,
      );
      if (deviceStatus.online) {
        const networkParts = [networkInterface];
        if (deviceStatus.ipAddress) {
          networkParts.push(deviceStatus.ipAddress);
        }
        if (deviceStatus.networkSpeedMbps) {
          networkParts.push(`${deviceStatus.networkSpeedMbps} Mbps`);
        }
        if (deviceStatus.networkSpeedMbps < 5) {
          info.addWarning(
            emojiSpan('🌐', `Unstable Network: (${deviceStatus.networkSpeedMbps} Mbps)`),
            null,
            true,
          );
        }
        communicationsParts.push(emojiSpan('🌐', `online (${networkParts.join('; ')})`));
      } else {
        info.addWarning(emojiSpan('🌐', `offline (${networkInterface})`), null, true);
      }

      // Wifi
      if (_.has(deviceStatus, 'wifiPowerState')) {
        const wifiOn = deviceStatus.wifiPowerState === KioskWifiPowerState.On;
        const wifiSsid = deviceStatus.wifiSsid || '';
        let wifiStateStr = KioskWifiPowerStateHelper.name(deviceStatus.wifiPowerState);
        if (wifiOn) {
          const wifiEmoji = deviceStatus.wifiConnected ? '✅' : '🔴';
          wifiStateStr += ` ${emojiSpan(wifiEmoji, wifiSsid.length ? `(${wifiSsid})` : '')}`;
        }
        wifiStateStr = iconSpan('wifi', wifiStateStr);
        if (!wifiOn) {
          info.addWarning(wifiStateStr, null, true);
        } else {
          communicationsParts.push(wifiStateStr);
        }
      }

      const kioskPaymentSystem = app.location?.getKioskPaymentI9nSchema()?.system;

      // Bluetooth
      if (_.has(deviceStatus, 'bluetoothState')) {
        communicationsParts.push(
          iconSpan('bluetooth', KioskBluetoothStateHelper.name(deviceStatus.bluetoothState)),
        );
      }

      if (communicationsParts.length) {
        info.details.push(communicationsParts.join(' | '));
      }

      const enrollmentCount = deviceStatus.enrollmentCount || 0;
      if (enrollmentCount > 0) {
        info.warnings.push(
          `${enrollmentCount} unfinished enrollment${enrollmentCount === 1 ? '' : 's'}`,
        );
      }

      if (this._status.has('pusherState')) {
        const pusherState = this._status.get('pusherState');
        const pusherStr = `Pusher: ${KioskPusherStateHelper.name(pusherState)}`;
        if (pusherState !== KioskPusherState.Subscribed) {
          info.errors.push({
            text: pusherStr,
            tooltip: {
              text: 'This kiosk will not receive any notifications including when the day part changes.\nPlease, check your network settings.',
              isWarning: true,
            },
          });
        } else {
          info.details.push(pusherStr);
        }
      }

      if (isBite && this._status.isIOS()) {
        const asam =
          `${app.HtmlHelper.biteRightIcon} ASAM ${deviceStatus.isAsamEnabled ? 'On' : 'OFF'}` +
          ` (System: ${deviceStatus.isSystemAsamEnabled ? 'On' : 'OFF'})`;
        const asamIsGood =
          !location.isLive() || (deviceStatus.isAsamEnabled && deviceStatus.isSystemAsamEnabled);
        if (asamIsGood) {
          info.details.push(asam);
        } else {
          info.warnings.push(asam);
        }
      }

      if (deviceStatus.isVoiceOverEnabled) {
        if (this._status.get('isUsingMenu')) {
          info.warnings.push('VoiceOver Enabled');
        } else {
          info.errors.push('VoiceOver Enabled');
        }
      }

      if (deviceStatus.batteryState === KioskBatteryState.UNPLUGGED) {
        info.errors.push('A/C Unplugged');
      }

      if (
        deviceStatus.currentNetworkInterface === KioskNetworkInterface.WiFi &&
        deviceStatus.expectedNetworkInterface === KioskNetworkInterface.Ethernet
      ) {
        info.warnings.push('Ethernet Disconnected');
      }

      if (this._status.isIOS()) {
        if (deviceStatus.notificationsAuthorizationStatus !== 2) {
          let notificationStatus = 'Not determined';
          if (deviceStatus.notificationsAuthorizationStatus === 1) {
            notificationStatus = 'Denied';
          }
          notificationStatus = `Notifications authorization: ${notificationStatus}`;
          info.addWarning(notificationStatus);
        }
      }
      if (this._kiosk && this._status.get('prefix') !== this._kiosk.get('prefix')) {
        info.warnings.push(
          `Prefix mismatch: ${this._status.get('prefix')} vs ${this._kiosk.get('prefix')}.`,
        );
      }

      const expectedPrinterMacAddress =
        this._kiosk && this._kiosk.get('printerType') ? this._kiosk.get('printerMacAddress') : null;
      if (
        _.size(deviceStatus.visiblePrinterMacAddresses) ||
        _.size(deviceStatus.visibleUsbPeripherals)
      ) {
        info.extraTitle = 'see visible printers & peripherals';
        info.extras = [];

        // Printers
        _.map(deviceStatus.visiblePrinterMacAddresses, (macAddress) => {
          if (macAddress === (expectedPrinterMacAddress || '')) {
            macAddress += ' (expected)';
          }

          info.extras.push(macAddress);
        });

        // Printer peripherals
        _.each(deviceStatus.visiblePrinterPeripherals, (peripheral) => {
          if (peripheral.indexOf('TCPS:') === 0) {
            peripheral = peripheral.substr(5);
          }
          info.extras.push(peripheral);
        });

        _.each(deviceStatus.visibleUsbPeripherals, (peripheral) => {
          info.extras.push(peripheral);
        });
      } else {
        info.extraTitle = 'no visible printers';
      }

      this._renderDevice($ipad, info);

      // Peripherals
      const receiptPrinterStatus = this._status.get('kioskPrinterStatus');
      this._renderPrinterStatus($printer, receiptPrinterStatus, expectedPrinterMacAddress);
      this._renderPaymentStatus($payment, this._status.get('paymentTerminalStatus'));

      if (receiptPrinterStatus) {
        _.each(receiptPrinterStatus.peripheralStati, (peripheralStatus) => {
          this._renderPrinterPeripheralStatus(peripheralStatus);
        });
      }

      if (this._kiosk ? this._kiosk.get('hasHid') : this._status.get('hidStatus')) {
        this._renderUsbPeripheralStatus(this._status.get('hidStatus'), KioskPeripheralType.HID);
      }
      if (this._kiosk ? this._kiosk.get('hasMsr') : this._status.get('msrStatus')) {
        this._renderUsbPeripheralStatus(this._status.get('msrStatus'), KioskPeripheralType.MSR);
      }
      if (this._kiosk ? this._kiosk.get('hasScanner') : this._status.get('scannerStatus')) {
        this._renderUsbPeripheralStatus(
          this._status.get('scannerStatus'),
          KioskPeripheralType.Scanner,
        );
      }
      if (this._kiosk ? this._kiosk.get('hasScale') : this._status.get('scaleStatus')) {
        this._renderUsbPeripheralStatus(this._status.get('scaleStatus'), KioskPeripheralType.Scale);
      }
      if (
        this._kiosk ? this._kiosk.get('hasStormAudioNav') : this._status.get('stormAudioNavStatus')
      ) {
        this._renderUsbPeripheralStatus(
          this._status.get('stormAudioNavStatus'),
          KioskPeripheralType.StormAudioNav,
        );
      }
      if (
        deviceStatus.displayStatus?.externalTouchDisplayState &&
        deviceStatus.displayStatus.externalTouchDisplayState !==
          KioskExternalTouchDisplayState.NotRequired
      ) {
        const $touchDisplay = this._renderAdditionalPeripheral('touch-display', 'Touch Display');
        this._renderTouchDisplayStatus($touchDisplay, deviceStatus.displayStatus);
      }
      if (this._status.get('posStatus')) {
        this._renderPosStatus(this._status.get('posStatus'));
      }

      // Dropdown actions.
      const $dropdown = $ipad.find('.actions .dropdown-menu');
      if (this._kiosk) {
        const canReceivePusherNotifications = this._status.canReceivePusherNotifications();
        const usesSimpleMdm = this._kiosk.isIOS() && this._status;
        /** @type {KioskDeviceType} */
        const deviceType = this._kiosk.get('deviceType');

        if (canReceivePusherNotifications) {
          if (isBiteSupport) {
            this._addDropdownActionForNotification(
              $dropdown,
              KioskNotificationType.RequestScreenshot,
              {
                rightIcon: app.HtmlHelper.biteRightIcon,
              },
            );

            // Same rights gate as admin v2's live-view action (kiosk-policy.ts): BiteSupport.
            // Version gate: Android app v2.20.0+ with garcon 0.18.2+ — older builds can't
            // handle the live-view notifications.
            const liveViewDeviceStatus = this._status.get('deviceStatus');
            if (liveViewDeviceStatus && KioskHelper.kioskSupportsLiveView(liveViewDeviceStatus)) {
              const location = this._getLocation();
              if (location) {
                const liveViewUrl =
                  `/v2/${location.get('orgNormalizedName')}/${location.get('normalizedName')}` +
                  `/kiosks?kiosk=${this._kiosk.id}&mode=live-view`;
                this._addDropdownAction($dropdown, 'Live View ↗', {
                  url: liveViewUrl,
                  rightIcon: app.HtmlHelper.biteRightIcon,
                });
              }
            }
          }

          if (app.sessionUser.canManageKiosks()) {
            this._addDropdownActionForNotification($dropdown, KioskNotificationType.TestPrint, {
              rightIcon: app.HtmlHelper.resellerRightIcon,
            });
            this._addDropdownActionForNotification($dropdown, KioskNotificationType.MenuUpdate, {
              rightIcon: app.HtmlHelper.resellerRightIcon,
            });
          }
        }

        if (app.sessionUser.canManageKiosks()) {
          $dropdown.append('<hr /><li class="item header"><span>Advanced</span></li>');
        }

        if (
          isBite ||
          (app.sessionUser.canManageKiosks() &&
            KioskDeviceTypeHelper.mdmIsAccessibleByNonBiteUsers(deviceType))
        ) {
          this._addDropdownAction($dropdown, 'Open MDM ↗', {
            clickHandler: () => {
              if (!KioskDeviceTypeHelper.mdmSupportsOpeningDeviceUrl(deviceType)) {
                // EloView 4 has no per-device deep link, so we surface a config error that points
                // the user at the EloView 4 console. A device missing from EloView 4 throws
                // MdmDeviceNotFound from getEloView4AuthTokenAndDeviceDetails before we get here.
                // Every other normal MDM supports this
                const kioskIsInSubAccount = app.org.hasMdmSettingsForProvider(MdmProvider.EloView);
                let eloViewMessage = 'Go to EloView 4 (https://secure.eloview.com/main/devices)';
                if (kioskIsInSubAccount) {
                  eloViewMessage += ', find the appropriate sub-account,';
                }
                eloViewMessage += ` and search for ${this._kiosk.get('serialNumber')}.`;
                // Leave the message itself alone: the EloView Link extension reads the serial
                // straight out of this text, so rewording it breaks Open MDM for everyone who
                // has the extension until they pick up a new version from the store.
                new app.AlertView().show(
                  eloViewMessage,
                  isBite ? eloViewLinkExtensionPromoHtml() : '',
                );
                return;
              }

              app.showSavedToast('Redirecting momentarily!');
              this._kiosk.getMdmUrl((err, url) => {
                if (!err) {
                  window.open(url);
                }
              });
            },
            rightIcon: KioskDeviceTypeHelper.mdmIsAccessibleByNonBiteUsers(deviceType)
              ? app.HtmlHelper.resellerRightIcon
              : app.HtmlHelper.biteRightIcon,
          });
          if (KioskDeviceTypeHelper.mdmSupportsFetchingRemoteUrl(deviceType)) {
            this._addDropdownAction($dropdown, 'Open Remote Access ↗', {
              clickHandler: () => {
                app.showSavedToast('Redirecting momentarily!');
                this._kiosk.getMdmRemoteAccessUrl((err, url) => {
                  if (!err) {
                    window.open(url);
                  }
                });
              },
              rightIcon: KioskDeviceTypeHelper.mdmIsAccessibleByNonBiteUsers(deviceType)
                ? app.HtmlHelper.resellerRightIcon
                : app.HtmlHelper.biteRightIcon,
            });
          }
        }

        if (canReceivePusherNotifications) {
          if (isBiteSupport) {
            this._addDropdownActionForNotification($dropdown, KioskNotificationType.RequestLogs, {
              rightIcon: app.HtmlHelper.biteRightIcon,
            });
            this._addDropdownActionForNotification($dropdown, KioskNotificationType.RequestStatus, {
              rightIcon: app.HtmlHelper.biteRightIcon,
            });
            if (this._status.isGarcon()) {
              this._addDropdownActionForNotification(
                $dropdown,
                KioskNotificationType.ReturnToConfig,
                {
                  confirmation: (clickHandler) => {
                    const msg = `Are you sure you want to return ${this._peerId()} to the config screen?`;
                    if (window.confirm(msg)) {
                      clickHandler();
                    }
                  },
                  rightIcon: app.HtmlHelper.biteRightIcon,
                },
              );
            }
          }

          if (app.sessionUser.canManageKiosks() && this._status.isGarcon()) {
            this._addDropdownActionForNotification($dropdown, KioskNotificationType.RunSpeedTest, {
              rightIcon: app.HtmlHelper.resellerRightIcon,
            });
          }
          if (app.sessionUser.canManageKiosks() && !this._status.isIOS()) {
            this._addDropdownActionForNotification(
              $dropdown,
              KioskNotificationType.RunWhitelistTest,
              {
                rightIcon: app.HtmlHelper.resellerRightIcon,
              },
            );
          }

          if (isBiteSupport && usesSimpleMdm) {
            this._addDropdownActionForNotification($dropdown, KioskNotificationType.AppUpdate, {
              rightIcon: app.HtmlHelper.biteRightIcon,
            });
            this._addDropdownActionForNotification($dropdown, KioskNotificationType.EnableAsam, {
              rightIcon: app.HtmlHelper.biteRightIcon,
            });
            this._addDropdownActionForNotification($dropdown, KioskNotificationType.DisableAsam, {
              rightIcon: app.HtmlHelper.biteRightIcon,
            });
          }

          if (app.location) {
            // Garcon based kiosk will always pull latest config on exit and restart
            if (isBiteEng && (this._status.isIOS() || this._status.isAndroid())) {
              this._addDropdownActionForNotification(
                $dropdown,
                KioskNotificationType.ConfigUpdate,
                {
                  rightIcon: app.HtmlHelper.biteRightIcon,
                },
              );
            }

            if (
              isBiteSupport &&
              kioskPaymentSystem &&
              PaymentHelper.systemSupportsUpdatePushes(kioskPaymentSystem)
            ) {
              this._addDropdownActionForNotification(
                $dropdown,
                KioskNotificationType.UpdatePaymentTerminal,
                {
                  rightIcon: app.HtmlHelper.biteRightIcon,
                },
              );
            }

            if (
              isBiteSupport &&
              (kioskPaymentSystem === IntegrationSystem.FreedomPayKiosk ||
                kioskPaymentSystem === IntegrationSystem.ParPay ||
                kioskPaymentSystem === IntegrationSystem.AurusPay)
            ) {
              this._addDropdownActionForNotification(
                $dropdown,
                KioskNotificationType.UploadPaymentTerminalLogs,
                {
                  rightIcon: app.HtmlHelper.biteRightIcon,
                },
              );
            }

            if (
              isBiteSupport &&
              kioskPaymentSystem &&
              PaymentHelper.paymentIntegrationSupportsReboot(kioskPaymentSystem)
            ) {
              this._addDropdownActionForNotification(
                $dropdown,
                KioskNotificationType.RebootPaymentTerminal,
                {
                  confirmation: (clickHandler) => {
                    const msg = `Are you sure you want to reboot the payment terminal on ${this._peerId()}?`;
                    if (window.confirm(msg)) {
                      clickHandler();
                    }
                  },
                  rightIcon: app.HtmlHelper.biteRightIcon,
                },
              );
            }
          }
        }

        if (isBiteSupport && usesSimpleMdm) {
          this._addDropdownAction($dropdown, 'Move MDM Group ASAM', {
            clickHandler: () => {
              app.showSavedToast('Moving');
              this._kiosk.moveDeviceToMDMGroupASAM(true);
            },
            rightIcon: app.HtmlHelper.biteRightIcon,
          });
          this._addDropdownAction($dropdown, 'Move MDM Group SAM', {
            clickHandler: () => {
              app.showSavedToast('Moving');
              this._kiosk.moveDeviceToMDMGroupASAM(false);
            },
            rightIcon: app.HtmlHelper.biteRightIcon,
          });
        }

        if (app.sessionUser.canControlKiosks()) {
          if (
            app.location?.getKioskPaymentI9nSchema()?.apiPayment?.canUpdateTerminalIdleText &&
            this._kiosk.get('paymentTerminalModel') !== KioskPaymentTerminalModel.None
          ) {
            this._addDropdownAction($dropdown, 'Update Payment Terminal Idle Text', {
              clickHandler: () => {
                this._kiosk.updatePaymentTerminalIdleText();
              },
              confirmation: (clickHandler) => {
                const msg = `Are you sure you want to update the idle text for ${this._kiosk.displayName()}?`;
                if (window.confirm(msg)) {
                  clickHandler();
                }
              },
            });
          }
          // not dry
          this._addDropdownActionForNotification($dropdown, KioskNotificationType.RestartApp, {
            confirmation: (clickHandler) => {
              let msg = `Are you sure you want to restart the app on ${this._peerId()}?`;
              if (usesSimpleMdm && !this._status.get('deviceStatus').isAsamEnabled) {
                msg = `${this._peerId()} is not in ASAM!!!\nIt won't auto-restart\n${msg}`;
              }
              if (window.confirm(msg)) {
                clickHandler();
              }
            },
          });

          let kioskSupportsAppLogin = false;
          try {
            kioskSupportsAppLogin = KioskHelper.kioskSupportsAppLogin(
              this._status.get('deviceStatus'),
            );
          } catch (error) {
            Sentry.captureException(error, {
              kioskId: this._kiosk.id,
            });
          }

          if (isBiteOps && kioskSupportsAppLogin) {
            this._addDropdownActionForNotification($dropdown, KioskNotificationType.AppLogin, {
              confirmation: (clickHandler) => {
                let msg = `Are you sure you want to login to the app and enter the menu on ${this._peerId()}?`;
                if (window.confirm(msg)) {
                  clickHandler();
                }
              },
            });
          }

          let kioskSupportsAppLogout = false;
          try {
            kioskSupportsAppLogout = KioskHelper.kioskSupportsAppLogout(
              this._status.get('deviceStatus'),
            );
          } catch (error) {
            Sentry.captureException(error, {
              kioskId: this._kiosk.id,
            });
          }

          if (kioskSupportsAppLogout) {
            this._addDropdownActionForNotification($dropdown, KioskNotificationType.AppLogout, {
              confirmation: (clickHandler) => {
                let msg = `Are you sure you want to log out of the app on ${this._peerId()}? Once logged out, the app will no longer be online.`;
                if (window.confirm(msg)) {
                  clickHandler();
                }
              },
            });
          }
          if (usesSimpleMdm || !this._status.isIOS()) {
            this._addDropdownAction($dropdown, 'Restart Device', {
              isCritical: true,
              clickHandler: () => {
                this._kiosk.restartDeviceThroughMDM({
                  success: () => {
                    app.showSavedToast('Restart command sent!');
                  },
                });
              },
              confirmation: (clickHandler) => {
                const msg = `Are you sure you want to restart the device ${this._kiosk.displayName()}?`;
                if (window.confirm(msg)) {
                  clickHandler();
                }
              },
            });
          }
        }
      } else if (isBite) {
        this._addDropdownAction($dropdown, 'View JSON ⚒', {
          clickHandler: () => {
            const modelName = 'kiosk status';
            const jsonView = new app.ModelJsonView({
              model: this._status,
              altText: modelName,
              title: `${StringHelper.toTitleCase(modelName)}: ${this._status.displayName()}`,
            });
            app.modalManager.showModalWithView(jsonView);
            return false;
          },
          rightIcon: app.HtmlHelper.biteRightIcon,
        });
      }
    } else {
      $ipad.find('.timestamp').text('No status');
      $ipad.toggleClass('no-status', true);
      $printer.toggleClass('no-status', true);
      $payment.toggleClass('no-status', true);

      $ipad.find('.btn-group.on-off').remove();

      const deviceInfo = new DeviceInfoStruct(
        this._kiosk.get('name') || `Serial: ${this._kiosk.get('serialNumber')}`,
        true,
      );
      deviceInfo.iconUrl = KioskDeviceTypeHelper.kioskIconUrlFromType(
        this._kiosk.get('deviceType'),
      );
      if (this._kiosk.has('deviceType')) {
        deviceInfo.fineprint.push(KioskDeviceTypeHelper.name(this._kiosk.get('deviceType')));
      }
      this._renderDevice($ipad, deviceInfo);
      this._renderPrinterInventory($printer, this._kiosk);
      this._renderPaymentInventory($payment, this._kiosk);
    }

    if (deviceIdentifierHtml.length) {
      $ipad.find('.device-identifier').append(deviceIdentifierHtml);
      $ipad.find('.device-name').toggleClass('multi-line', true);
      $ipad.find('.device-icon').toggleClass('multi-line', true);
    }

    this._cleanupDropdownActions();

    return this;
  },
});
app.KioskStatusView.Events = {
  ViewWillExpand: 'viewWillExpand',
  ViewWillCollapse: 'viewWillCollapse',
};

app.KioskStatusHistoryListView = app.BaseListView.extend({
  initialize(options) {
    app.BaseListView.prototype.initialize.apply(this, arguments);
    this.listenTo(this.collection, 'reset update', this._renderHistoryNotices);
  },

  _renderHistoryNotices() {
    this.$('.history-unavailable, .history-starts-at').remove();
    if (this.collection.historyUnavailable) {
      this.$('.card > .card-header').after(app.HtmlHelper.historyUnavailableHtml());
    }
    if (this.collection.historyStartsAt !== null) {
      this.$('.card > .card-header').after(
        app.HtmlHelper.historyStartsAtHtml(this.collection.historyStartsAt),
      );
    }
  },

  render() {
    app.BaseListView.prototype.render.apply(this, arguments);
    this._renderHistoryNotices();
    return this;
  },
});
